import React, { useState } from "react";
import api from "@/lib/api";
import { useLang, t } from "@/context/LangContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calculator as CalcIcon } from "lucide-react";

const CalculatorInput = ({ label, value, setValue, step = "any" }) => (
  <label className="block mb-4">
    <div className="overline text-white/40 mb-2">{label}</div>
    <Input
      type="number"
      step={step}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      className="bg-white/5 border-white/10 font-mono h-12"
      data-testid={`calc-input-${label.replace(/\s/g, '-').toLowerCase()}`}
    />
  </label>
);

const ResultCard = ({ result, lang }) => {
  if (!result) return null;
  if (result.error) return <div className="border border-rose-500/30 bg-rose-500/10 text-rose-300 rounded-xl p-6 mt-6" data-testid="calc-error">{result.error}</div>;
  return (
    <div className="border border-white/10 rounded-2xl p-8 mt-6 bg-[#0d0d0d]" data-testid="calc-result">
      <div className="overline text-white/40 mb-2">{t(lang, "Harga Wajar", "Fair Value")}</div>
      <div className="font-mono text-5xl font-bold">{result.fair_value?.toLocaleString()}</div>
      {result.method && <div className="mt-2 text-white/50 text-sm">{t(lang, "Metode", "Method")}: {result.method}</div>}
    </div>
  );
};

const Calculators = () => {
  const { lang } = useLang();

  // DCF state
  const [dcf, setDcf] = useState({ fcf: "500", growth_rate: "8", discount_rate: "10", terminal_growth: "2.5", years: "10" });
  const [dcfResult, setDcfResult] = useState(null);

  // PER
  const [per, setPer] = useState({ eps: "10", fair_per: "15" });
  const [perResult, setPerResult] = useState(null);

  // PBV
  const [pbv, setPbv] = useState({ book_value: "50", fair_pbv: "2" });
  const [pbvResult, setPbvResult] = useState(null);

  // Graham
  const [graham, setGraham] = useState({ eps: "10", book_value: "50" });
  const [grahamResult, setGrahamResult] = useState(null);

  // DDM
  const [ddm, setDdm] = useState({ dividend: "2", growth_rate: "5", required_return: "10" });
  const [ddmResult, setDdmResult] = useState(null);

  const parseNum = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, parseFloat(v)]));

  const runDcf = async () => {
    const r = await api.post("/calc/dcf", parseNum(dcf));
    setDcfResult(r.data);
  };
  const runPer = async () => setPerResult((await api.post("/calc/per", parseNum(per))).data);
  const runPbv = async () => setPbvResult((await api.post("/calc/pbv", parseNum(pbv))).data);
  const runGraham = async () => setGrahamResult((await api.post("/calc/graham", parseNum(graham))).data);
  const runDdm = async () => setDdmResult((await api.post("/calc/ddm", parseNum(ddm))).data);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="calculators-page">
      <div className="overline text-white/40 mb-3">{t(lang, "5 Metode Valuasi Saham", "5 Valuation Methods")}</div>
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-3">
        {t(lang, "Kalkulator Harga Wajar", "Fair Value Calculator")}
      </h1>
      <p className="text-white/60 max-w-2xl mb-10">
        {t(lang, "Hitung nilai intrinsik saham menggunakan 5 metode klasik. Kombinasikan hasilnya untuk mendapatkan gambaran valuasi yang lebih akurat.", "Estimate intrinsic value using 5 classical methods. Combine results for a fuller valuation picture.")}
      </p>

      <Tabs defaultValue="dcf" className="w-full">
        <TabsList className="bg-white/5 border border-white/10 p-1 h-auto flex-wrap justify-start" data-testid="calc-tabs">
          <TabsTrigger value="dcf" data-testid="tab-dcf" className="data-[state=active]:bg-white data-[state=active]:text-black">DCF</TabsTrigger>
          <TabsTrigger value="per" data-testid="tab-per" className="data-[state=active]:bg-white data-[state=active]:text-black">PER</TabsTrigger>
          <TabsTrigger value="pbv" data-testid="tab-pbv" className="data-[state=active]:bg-white data-[state=active]:text-black">PBV</TabsTrigger>
          <TabsTrigger value="graham" data-testid="tab-graham" className="data-[state=active]:bg-white data-[state=active]:text-black">Graham</TabsTrigger>
          <TabsTrigger value="ddm" data-testid="tab-ddm" className="data-[state=active]:bg-white data-[state=active]:text-black">DDM</TabsTrigger>
        </TabsList>

        <TabsContent value="dcf" className="mt-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-white/10 rounded-2xl p-8">
              <h3 className="font-display font-bold text-2xl mb-2">Discounted Cash Flow</h3>
              <p className="text-sm text-white/50 mb-6">{t(lang, "Proyeksikan FCF dan diskon ke nilai sekarang.", "Project FCF and discount to present value.")}</p>
              <CalculatorInput label={t(lang, "FCF per Saham", "FCF per Share")} value={dcf.fcf} setValue={(v) => setDcf({ ...dcf, fcf: v })} />
              <CalculatorInput label={t(lang, "Growth Rate (%)", "Growth Rate (%)")} value={dcf.growth_rate} setValue={(v) => setDcf({ ...dcf, growth_rate: v })} />
              <CalculatorInput label={t(lang, "Discount Rate (%)", "Discount Rate (%)")} value={dcf.discount_rate} setValue={(v) => setDcf({ ...dcf, discount_rate: v })} />
              <CalculatorInput label={t(lang, "Terminal Growth (%)", "Terminal Growth (%)")} value={dcf.terminal_growth} setValue={(v) => setDcf({ ...dcf, terminal_growth: v })} />
              <CalculatorInput label={t(lang, "Tahun Proyeksi", "Years")} value={dcf.years} setValue={(v) => setDcf({ ...dcf, years: v })} step="1" />
              <Button onClick={runDcf} data-testid="run-dcf" className="w-full bg-white text-black hover:bg-white/90 h-12 rounded-full">
                <CalcIcon size={16} className="mr-2" /> {t(lang, "Hitung", "Calculate")}
              </Button>
            </div>
            <div>
              <ResultCard result={dcfResult} lang={lang} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="per" className="mt-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-white/10 rounded-2xl p-8">
              <h3 className="font-display font-bold text-2xl mb-2">P/E Ratio Method</h3>
              <p className="text-sm text-white/50 mb-6">Fair Value = EPS × Fair P/E</p>
              <CalculatorInput label={t(lang, "EPS (Laba per Saham)", "EPS (Earnings per Share)")} value={per.eps} setValue={(v) => setPer({ ...per, eps: v })} />
              <CalculatorInput label={t(lang, "Fair P/E (Rata-rata Industri)", "Fair P/E (Industry Avg)")} value={per.fair_per} setValue={(v) => setPer({ ...per, fair_per: v })} />
              <Button onClick={runPer} data-testid="run-per" className="w-full bg-white text-black hover:bg-white/90 h-12 rounded-full">{t(lang, "Hitung", "Calculate")}</Button>
            </div>
            <div><ResultCard result={perResult} lang={lang} /></div>
          </div>
        </TabsContent>

        <TabsContent value="pbv" className="mt-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-white/10 rounded-2xl p-8">
              <h3 className="font-display font-bold text-2xl mb-2">P/B Ratio Method</h3>
              <p className="text-sm text-white/50 mb-6">Fair Value = BVPS × Fair P/B</p>
              <CalculatorInput label={t(lang, "Book Value per Share", "Book Value per Share")} value={pbv.book_value} setValue={(v) => setPbv({ ...pbv, book_value: v })} />
              <CalculatorInput label={t(lang, "Fair P/B", "Fair P/B")} value={pbv.fair_pbv} setValue={(v) => setPbv({ ...pbv, fair_pbv: v })} />
              <Button onClick={runPbv} data-testid="run-pbv" className="w-full bg-white text-black hover:bg-white/90 h-12 rounded-full">{t(lang, "Hitung", "Calculate")}</Button>
            </div>
            <div><ResultCard result={pbvResult} lang={lang} /></div>
          </div>
        </TabsContent>

        <TabsContent value="graham" className="mt-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-white/10 rounded-2xl p-8">
              <h3 className="font-display font-bold text-2xl mb-2">Graham Number</h3>
              <p className="text-sm text-white/50 mb-6">√(22.5 × EPS × BVPS)</p>
              <CalculatorInput label="EPS" value={graham.eps} setValue={(v) => setGraham({ ...graham, eps: v })} />
              <CalculatorInput label="Book Value per Share" value={graham.book_value} setValue={(v) => setGraham({ ...graham, book_value: v })} />
              <Button onClick={runGraham} data-testid="run-graham" className="w-full bg-white text-black hover:bg-white/90 h-12 rounded-full">{t(lang, "Hitung", "Calculate")}</Button>
            </div>
            <div><ResultCard result={grahamResult} lang={lang} /></div>
          </div>
        </TabsContent>

        <TabsContent value="ddm" className="mt-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-white/10 rounded-2xl p-8">
              <h3 className="font-display font-bold text-2xl mb-2">Dividend Discount Model</h3>
              <p className="text-sm text-white/50 mb-6">Fair Value = D × (1+g) / (r-g) — Gordon Growth</p>
              <CalculatorInput label={t(lang, "Dividen Tahunan Terakhir", "Latest Annual Dividend")} value={ddm.dividend} setValue={(v) => setDdm({ ...ddm, dividend: v })} />
              <CalculatorInput label={t(lang, "Growth Rate Dividen (%)", "Dividend Growth Rate (%)")} value={ddm.growth_rate} setValue={(v) => setDdm({ ...ddm, growth_rate: v })} />
              <CalculatorInput label={t(lang, "Required Return (%)", "Required Return (%)")} value={ddm.required_return} setValue={(v) => setDdm({ ...ddm, required_return: v })} />
              <Button onClick={runDdm} data-testid="run-ddm" className="w-full bg-white text-black hover:bg-white/90 h-12 rounded-full">{t(lang, "Hitung", "Calculate")}</Button>
            </div>
            <div><ResultCard result={ddmResult} lang={lang} /></div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Calculators;
